import React from 'react';
import DropdownRenderer from './DropdownRenderer';

// Mental status exam fields are intentionally not restricted to a single
// option: clinicians can select any combination of the offered options or
// write their own description via "Other (specify)". Values are always saved
// as arrays on the child field ids.
const MSEFieldGroup = ({ fields, formData, onChange, errors, readOnly, title = 'Mental Status Examination' }) => {
  const fieldValue = (field) => {
    const raw = formData[field.field_id] ?? field.default_value ?? [];
    return Array.isArray(raw) ? raw : raw === '' ? [] : [raw];
  };

  const handleQuickFill = (fillType) => {
    if (readOnly) return;

    const updates = {};

    if (fillType === 'not_assessed') {
      fields.forEach(field => {
        updates[field.field_id] = ['Not Assessed'];
      });
    } else if (fillType === 'wnl') {
      const wnlMappings = {
        'mse_orientation': 'X3: Oriented to Person, Place, and Time',
        'mse_appearance': 'Well-groomed and appropriately dressed',
        'mse_motor_activity': 'Normal motor activity',
        'mse_speech': 'Normal rate, rhythm, and volume',
        'mse_mood': 'Euthymic',
        'mse_affect': 'Euthymic and congruent',
        'mse_thought_process': 'Linear and goal-directed',
        'mse_thought_content': 'No abnormal thought content',
        'mse_perceptions': 'No perceptual disturbances',
        'mse_cognition': 'Alert and oriented',
        'mse_insight': 'Good insight into illness',
        'mse_judgment': 'Good judgment',
        'orientation': 'X3: Oriented to Person, Place, and Time',
        'general_appearance': 'Well-groomed and appropriately dressed',
        'motor_activity': 'Normal motor activity',
        'interview_behavior': 'Cooperative and engaged',
        'speech': 'Normal rate, rhythm, and volume',
        'mood': 'Euthymic',
        'affect': 'Euthymic and congruent',
        'thought_process': 'Linear and goal-directed',
        'thought_content': 'No abnormal thought content',
        'perceptual_disturbances': 'No perceptual disturbances',
        'cognition': 'Alert and oriented',
        'insight': 'Good insight into illness',
        'judgment': 'Good judgment',
        'post_orientation': 'Fully oriented x3',
        'post_coherence': 'Fully coherent',
        'post_emotional_state': 'Calm and grounded',
      };

      fields.forEach(field => {
        const wnlValue = wnlMappings[field.field_id];
        if (wnlValue) {
          updates[field.field_id] = [wnlValue];
        }
      });
    }

    onChange(null, updates);
  };

  return (
    <div className="border border-gray-300 rounded-lg p-4 mb-4">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold">{title}</h3>
        {!readOnly && (
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => handleQuickFill('wnl')}
              className="px-3 py-1 text-sm bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors"
              title="Mark all MSE fields as Within Normal Limits"
            >
              Mark All Within Normal Limits
            </button>
            <button
              type="button"
              onClick={() => handleQuickFill('not_assessed')}
              className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
              title="Mark all MSE fields as Not Assessed"
            >
              Mark All Not Assessed
            </button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2">
        {fields.map(field => (
          <div key={field.field_id} className="min-w-0">
            <DropdownRenderer
              field={field}
              value={fieldValue(field)}
              onChange={(value) => onChange(field.field_id, value)}
              error={errors[field.field_id]}
              readOnly={readOnly}
              multiple
              freetext
              placeholder="Not Assessed"
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default MSEFieldGroup;
